import React, { useContext, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import isMatch from 'lodash/isMatch';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AuthContext, type AuthContextValue } from '~/auth';

import { Button, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

import UserBreadcrumbs from '~/components/Common/Elements/UserBreadcrumbs';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';

import { canViewOrderEditLogs } from '~/utils/canViewOrderEditLogs';

import { clearOrderError, fetchOrderAndPurchaserData, fetchOrderData, submitUpdatesForm } from '../../../actions/order';
import { fetchUserData } from '../../../actions/user';
import {
  EDIT_ORDER_TITLE,
  ORDER_STATUS_CANCELLED,
  ORDER_STATUS_COMPLETED,
  ORDER_STATUS_PENDING,
  OrderPatchOperations,
} from '../../../consts/order';
import { orderSelector } from '../../../selectors/orderSelector';
import { userSelector } from '../../../selectors/userSelector';
import { createPatchPayload } from '../../../utils/createPatchPayload';
import OrderForm from '../../Common/Forms/OrderForm';
import PermissionedComponent from '../../Common/PermissionedComponent';
import Spinner from '../../Common/Spinner';
import { withTenant } from '../../hoc';

import OrderEditLogs from './OrderEditLogs';

type OrderEditContainerState = {
  fk_purchaser_id: {
    op: OrderPatchOperations;
    value: string;
  };
};

function OrderEditContainer(props) {
  const { user } = useContext<AuthContextValue>(AuthContext);
  const { user: currentUser } = useCurrentUser();
  const { id_orders: orderId } = useParams<{ id_orders: string }>();
  const { fetchUserData, fetchOrderAndPurchaserData, submitUpdatesForm, clearOrderError } = props;
  const { error, getPurchaser, isLoading, isUpdating, order, tenant } = props;

  const { push } = useAppNavigation();
  const queryClient = useQueryClient();
  // TODO: Fix this {}. Setting this undefined/null is causing runtime issues.
  const [state, setState] = useState<OrderEditContainerState>({} as OrderEditContainerState);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const prevUserContextRef = useRef(null);

  const handleChange = (e, op) => {
    setState((prevState) => ({
      ...prevState,
      [e.target.name]: { op, value: e.target.value },
    }));
  };

  const handleChangePurchaser = async (op: OrderPatchOperations, purchaserId: string) => {
    await fetchUserData(purchaserId);
    setState((prevState) => ({
      ...prevState,
      fk_purchaser_id: { op, value: purchaserId },
    }));
  };

  const handleChangeOrderStatus = (newStatus: string) => {
    clearOrderError();
    setState((prevState) => {
      // If returning to pending, remove the orderStatus entry from state
      // This prevents sending any operation for a non-change
      if (newStatus === ORDER_STATUS_PENDING) {
        const newState = { ...prevState };
        delete (newState as Record<string, unknown>).orderStatus;
        return newState;
      }

      // Map the selected status to the appropriate operation
      let op: OrderPatchOperations;
      if (newStatus === ORDER_STATUS_COMPLETED) {
        op = OrderPatchOperations.PATCH_MANUAL_COMPLETE_ORDER;
      } else if (newStatus === ORDER_STATUS_CANCELLED) {
        op = OrderPatchOperations.PATCH_MANUAL_CANCEL_ORDER;
      } else {
        return prevState;
      }

      return {
        ...prevState,
        orderStatus: { op, value: newStatus },
      };
    });
  };

  const submitOrderEditUpdatesForm = async () => {
    const payload = createPatchPayload(state ?? {});
    const success = await submitUpdatesForm(orderId, payload);
    if (success) {
      await queryClient.invalidateQueries({ queryKey: ['orderEditLogs'] });
      push(`/purchases/${orderId}`);
    }
  };

  const hasOrderStatusChange = !!(state as Record<string, unknown>).orderStatus;

  const handleSave = () => {
    if (hasOrderStatusChange) {
      setConfirmDialogOpen(true);
    } else {
      submitOrderEditUpdatesForm();
    }
  };

  const handleConfirmSave = async () => {
    setConfirmDialogOpen(false);
    await submitOrderEditUpdatesForm();
  };

  const isDirty = (order: App.Order) => {
    const stateEntries = Object.entries(state);
    if (stateEntries.length === 0) {
      return false;
    }

    const orderUpdates = {};
    for (const entry of stateEntries) {
      // Map orderStatus to status for comparison with order object
      const key = entry[0] === 'orderStatus' ? 'status' : entry[0];
      orderUpdates[key] = entry[1].value;
    }
    return !isMatch(order, orderUpdates);
  };

  useEffect(() => {
    if (user) {
      prevUserContextRef.current = user;
      fetchOrderAndPurchaserData(orderId);
    }
  }, [user, orderId, fetchOrderAndPurchaserData]);

  useEffect(() => {
    if (prevUserContextRef.current && !isMatch(prevUserContextRef.current, user)) {
      prevUserContextRef.current = user;
      fetchOrderAndPurchaserData(orderId);
    }
  }, [user, orderId, fetchOrderAndPurchaserData]);

  return (
    <Container maxWidth="xl">
      <UserBreadcrumbs orderId={orderId} currentPage="Edit" />
      <h1 className="page-header">{EDIT_ORDER_TITLE}</h1>

      {isLoading ? (
        <Spinner />
      ) : (
        <OrderForm
          order={
            order // temp changes are saved to react state
          }
          purchaser={state.fk_purchaser_id ? getPurchaser(state.fk_purchaser_id.value) : order.purchaser}
          handleChange={handleChange}
          handleChangePurchaser={handleChangePurchaser}
          handleChangeOrderStatus={handleChangeOrderStatus}
          tenant={tenant}
          onSave={handleSave}
          isLoading={isLoading}
          isUpdating={isUpdating}
          isDirty={isDirty(order)}
          error={error}
        />
      )}

      <PermissionedComponent>
        {!isLoading && order && canViewOrderEditLogs(currentUser ?? user) && (
          <OrderEditLogs orderId={orderId} brand={tenant.brand} />
        )}
      </PermissionedComponent>

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Order Status Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to permanently change the order status. This action cannot be reversed. Are you sure you want
            to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmSave} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

const mapStateToProps = (state, ownProps) => {
  const { match } = ownProps;
  const orderId = match.params.id_orders;
  const order = orderSelector(state, orderId);
  const getPurchaser = (purchaserId: string) => userSelector(state, purchaserId);
  const { isLoading: isLoadingOrder, isUpdating, error: orderError } = state.order;
  const { isLoading: isLoadingUser, error: userError } = state.user;
  return {
    error: userError || orderError,
    getPurchaser,
    isLoading: isLoadingOrder || isLoadingUser,
    isUpdating,
    order,
  };
};

const mapDispatchToProps = {
  clearOrderError,
  fetchOrderData,
  fetchOrderAndPurchaserData,
  submitUpdatesForm,
  fetchUserData,
};

export default withTenant(connect(mapStateToProps, mapDispatchToProps)(OrderEditContainer));
